import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import TopBar from "../TopBar";
import Footerr from "../Footerr/Footerr";

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);

  const orderId = searchParams.get("order_id");
  const statusParam = searchParams.get("status");
  const API_BASE_URL = "https://domains-marketplace-backend.vercel.app";

  const isVerifying = useRef(false);
  const hasShownToast = useRef({ loading: false, result: false });
  const verificationTimeout = useRef(null);
  const pollingAttempts = useRef(0);
  const hasCompletedVerification = useRef(false); // Prevent any re-processing
  const hasInitialized = useRef(false); // Track if verification has been initiated
  const MAX_POLLING_ATTEMPTS = 8; // 8 attempts * 2 seconds = 16 seconds max (close to 15s)

  const verifyPayment = useCallback(async () => {
    // Double-check if verification already completed
    if (hasCompletedVerification.current) {
      return;
    }

    if (isVerifying.current) {
      return;
    }

    isVerifying.current = true;
    pollingAttempts.current += 1;

    // Stop polling if max attempts reached - treat as success
    if (pollingAttempts.current > MAX_POLLING_ATTEMPTS) {

      if (!hasCompletedVerification.current) {
        hasCompletedVerification.current = true;
        hasShownToast.current.result = true;
        setVerifying(false);
        isVerifying.current = false;

        sessionStorage.setItem(`payment_completed_${orderId}`, 'true');

        if (verificationTimeout.current) {
          clearTimeout(verificationTimeout.current);
          verificationTimeout.current = null;
        }

        setPaymentStatus("PAID");

        // Show success toast and redirect
        toast.success("Payment successful!", { id: "success-toast-final", duration: 3000 });

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate("/dashboard/domains");
        }, 2000);
      }
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/payment/verify/${orderId}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        setVerifying(false);
        isVerifying.current = false;
        if (!hasShownToast.current.result) {
          hasShownToast.current.result = true;
          toast.error(data.message || "Payment verification failed", { id: "error-toast" });
        }
        setPaymentStatus("FAILED");
        return;
      }

      const currentStatus = data.orderStatus;

      // Backend says payment not completed
      if (data.shouldRedirect) {
        setVerifying(false);
        isVerifying.current = false;
        if (!hasShownToast.current.result) {
          hasShownToast.current.result = true;
          toast.error("Payment was not completed", { id: "redirect-toast" });
        }
        setPaymentStatus("CANCELLED");
        return;
      }

      // Handle different statuses
      if (currentStatus === "PAID") {
        // If allocated OR payment is confirmed as PAID (even without allocation after 5 attempts)
        const shouldComplete = data.allocated || pollingAttempts.current >= 5;

        if (shouldComplete) {

          // Immediately set ALL flags to prevent re-entry
          if (!hasCompletedVerification.current) {
            hasCompletedVerification.current = true;
            hasShownToast.current.result = true;
            setVerifying(false);
            isVerifying.current = false;

            // Store completion in sessionStorage to survive remounts
            sessionStorage.setItem(`payment_completed_${orderId}`, 'true');

            // Clear any pending timeouts
            if (verificationTimeout.current) {
              clearTimeout(verificationTimeout.current);
              verificationTimeout.current = null;
            }

            // Update state
            setPaymentStatus("PAID");
            setOrderDetails(data);

            // Show success toast and redirect to dashboard
            toast.success("Payment successful! Redirecting...", {
              id: "success-toast-final",
              duration: 3000
            });

            // Auto-redirect to dashboard after 2 seconds
            setTimeout(() => {
              navigate("/dashboard/domains");
            }, 2000);
          }
        } else {
          isVerifying.current = false;
          verificationTimeout.current = setTimeout(() => verifyPayment(), 2000);
        }
      } else if (currentStatus === "ACTIVE") {
        isVerifying.current = false;
        verificationTimeout.current = setTimeout(() => verifyPayment(), 2000);
      } else if (
        currentStatus === "FAILED" ||
        currentStatus === "USER_DROPPED" ||
        currentStatus === "CANCELLED"
      ) {
        setVerifying(false);
        isVerifying.current = false;
        if (!hasShownToast.current.result) {
          hasShownToast.current.result = true;
          toast.error("Payment was not completed", { id: "cancelled-toast" });
        }
        setPaymentStatus("CANCELLED");
      } else {
        setVerifying(false);
        isVerifying.current = false;
        if (!hasShownToast.current.result) {
          hasShownToast.current.result = true;
          toast.error("Unable to verify payment", { id: "unknown-toast" });
        }
        setPaymentStatus("FAILED");
      }
    } catch (error) {
      setVerifying(false);
      isVerifying.current = false;
      if (!hasShownToast.current.result) {
        hasShownToast.current.result = true;
        toast.error(`Payment verification failed: ${error.message}`, { id: "catch-error-toast" });
      }
      setPaymentStatus("FAILED");
    }
  }, [orderId, navigate]);

  useEffect(() => {
    if (!orderId) {
      toast.dismiss();
      toast.error("Invalid payment session");
      navigate("/marketplace");
      return;
    }

    // Check sessionStorage for completion status
    const completionKey = `payment_completed_${orderId}`;
    const isCompleted = sessionStorage.getItem(completionKey);

    if (isCompleted === 'true') {
      hasCompletedVerification.current = true;
      hasInitialized.current = true;
      setVerifying(false);
      setPaymentStatus("PAID");
      navigate("/dashboard/domains");
      return;
    }

    // Prevent re-initialization if already started
    if (hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;

    // Clear all toasts on mount
    toast.dismiss();
    hasShownToast.current = { loading: false, result: false };
    hasCompletedVerification.current = false;
    pollingAttempts.current = 0;

    // If status already known from URL
    if (statusParam === "failed") {
      setPaymentStatus("FAILED");
      setVerifying(false);
      if (!hasShownToast.current.result) {
        toast.error("Payment failed");
        hasShownToast.current.result = true;
      }
      return;
    }

    if (statusParam === "cancelled") {
      setPaymentStatus("CANCELLED");
      setVerifying(false);
      if (!hasShownToast.current.result) {
        toast.error("Payment cancelled");
        hasShownToast.current.result = true;
      }
      return;
    }

    // Start verification
    verifyPayment();

    return () => {
      if (verificationTimeout.current) {
        clearTimeout(verificationTimeout.current);
        verificationTimeout.current = null;
      }
      isVerifying.current = false;
    };
  }, [orderId, statusParam, navigate, verifyPayment]);

  const handleGoToDashboard = () => {
    toast.dismiss();
    // Clean up sessionStorage when navigating away successfully
    if (orderId) {
      sessionStorage.removeItem(`payment_completed_${orderId}`);
    }
    navigate("/dashboard/domains");
  };

  const handleBackToMarketplace = () => {
    toast.dismiss();
    // Clean up sessionStorage when navigating away successfully
    if (orderId) {
      sessionStorage.removeItem(`payment_completed_${orderId}`);
    }
    navigate("/marketplace");
  };

  const handleRetryPayment = () => {
    toast.dismiss();
    // Clean up sessionStorage when retrying
    if (orderId) {
      sessionStorage.removeItem(`payment_completed_${orderId}`);
    }
    navigate(-2);
  };

  return (
    <div className="bg-[#f9f8f4] min-h-screen flex flex-col">
      <TopBar />

      <div className="flex-1 flex justify-center items-center py-16 px-4 mt-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl shadow-2xl border border-[#e2d1a6]/60 w-full max-w-2xl p-10 text-center"
        >
          {verifying ? (
            <>
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-[#b99653] mb-6"></div>
                <h2 className="text-2xl font-bold text-[#101c4c] mb-3">
                  Verifying Payment...
                </h2>
                <p className="text-gray-600">
                  Please wait while we confirm your payment
                </p>
                <p className="text-sm text-gray-500 mt-2 break-all px-4">
                  Order: {orderId}
                </p>
              </div>
            </>
          ) : paymentStatus === "PAID" ? (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <svg
                  className="w-12 h-12 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </motion.div>

              <h1 className="text-3xl font-extrabold text-[#101c4c] mb-3">
                Payment Successful! 🎉
              </h1>
              <p className="text-gray-600 text-lg mb-2">
                Your domain has been allocated to your account
              </p>

              {orderDetails && (
                <div className="bg-[#fff9f1] border border-[#f2e1b3] rounded-2xl p-6 mt-6 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-left">
                    <div>
                      <p className="text-sm text-gray-600">Order ID</p>
                      <p className="font-semibold text-[#101c4c] text-xs break-all">
                        {orderDetails.orderId}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Amount Paid</p>
                      <p className="font-semibold text-[#101c4c]">
                        ₹{orderDetails.orderAmount?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <button
                  onClick={handleGoToDashboard}
                  className="px-8 py-3 font-bold text-white rounded-lg shadow-md transition-all hover:scale-105"
                  style={{
                    background:
                      "linear-gradient(90deg, #b99653 0%, #e3b757 50%, #b99653 100%)",
                  }}
                >
                  View My Domains
                </button>
                <button
                  onClick={handleBackToMarketplace}
                  className="px-8 py-3 font-semibold text-[#101c4c] bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
                >
                  Continue Shopping
                </button>
              </div>
            </>
          ) : paymentStatus === "CANCELLED" ? (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <svg
                  className="w-12 h-12 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </motion.div>

              <h1 className="text-3xl font-extrabold text-[#101c4c] mb-3">
                Payment Cancelled
              </h1>
              <p className="text-gray-600 text-lg mb-6">
                Your payment was not completed. No charges were made.
              </p>

              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-orange-800 break-all">
                  Order: {orderId}
                  <br />
                  Status: Cancelled
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <button
                  onClick={handleRetryPayment}
                  className="px-8 py-3 font-bold text-white rounded-lg shadow-md transition-all hover:scale-105"
                  style={{
                    background:
                      "linear-gradient(90deg, #b99653 0%, #e3b757 50%, #b99653 100%)",
                  }}
                >
                  Try Again
                </button>
                <button
                  onClick={handleBackToMarketplace}
                  className="px-8 py-3 font-semibold text-[#101c4c] bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
                >
                  Browse Marketplace
                </button>
              </div>
            </>
          ) : (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <svg
                  className="w-12 h-12 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </motion.div>

              <h1 className="text-3xl font-extrabold text-[#101c4c] mb-3">
                Payment Failed
              </h1>
              <p className="text-gray-600 text-lg mb-6">
                Unfortunately, your payment could not be processed.
              </p>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-red-800 break-all">
                  Order: {orderId}
                  <br />
                  Status: Failed
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <button
                  onClick={handleRetryPayment}
                  className="px-8 py-3 font-bold text-white rounded-lg shadow-md transition-all hover:scale-105"
                  style={{
                    background:
                      "linear-gradient(90deg, #b99653 0%, #e3b757 50%, #b99653 100%)",
                  }}
                >
                  Try Again
                </button>
                <button
                  onClick={handleBackToMarketplace}
                  className="px-8 py-3 font-semibold text-[#101c4c] bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
                >
                  Browse Marketplace
                </button>
              </div>
            </>
          )}

          <p className="text-xs text-gray-500 mt-8">
            Having issues?{" "}
            <span
              className="text-[#b99653] cursor-pointer hover:underline"
              onClick={() => navigate("/contact")}
            >
              Contact Support
            </span>
          </p>
        </motion.div>
      </div>

      <Footerr />
    </div>
  );
}

export default PaymentSuccess;
